/**
 * The studio print page (sections/btf-studio-print.liquid): staff only.
 *
 * 1. GET /apps/btf-ai/session (Shopify's app proxy) names the signed-in customer and gives a
 *    ticket. The AI server decides who is staff: GET {api}/studio/order with no name answers 403
 *    for anyone else and 400 ("which order?") for staff, without touching an order.
 * 2. GET {api}/studio/order?name=BTF7130 lists the order's personalised lines and their
 *    properties (apps/btf-ai/worker/src/index.js, studioOrder).
 * 3. A Pet portrait line is rebuilt: each chosen drawing is made 4K (POST {api}/enlarge, about
 *    35 s and 19p the first time, kept after that), then a bare Pet portrait maker is put out of
 *    sight on the page (the section's template[data-sp-maker]) and its engine
 *    (assets/btf-maker.js, #studioHook) restores the board from the line and makes the print
 *    files with the 4K drawings. The PDF and the flat JPEG are offered as downloads, with each picture's pixels and
 *    dpi at the board's size.
 *    Other lines link the print files already on the order.
 * 4. A Made in my town line carries only the words, so the board is drawn again from them with
 *    the town generator's own layout (assets/btf-town.js, printFace) and offered as a download.
 *    Its face, Gill Sans, is not licensed into the site, so this only runs on a computer that has
 *    it installed (the studio's Mac).
 *
 * ?studio_test=1 shows a box to paste a basket line's properties (from /cart.js) instead of an
 * order, for testing on a preview theme.
 */

const root = document.querySelector('[data-btf-studio]');
const $ = (sel) => root.querySelector(sel);
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const setup = JSON.parse(root.querySelector('[data-sp-setup]').textContent);
const params = new URLSearchParams(window.location.search);

// Property names the makers use for their print files and previews
const PREVIEW_PROPS = ['_artwork_preview', 'Your artwork', '_preview', 'Preview'];
const PRINT_FILE = '_Artwork (print from this)';
const OLD_PRINT_FILE = '_Print file (board + 5 mm bleed, 150 dpi)';
const FILE_LABELS = {
  [PRINT_FILE]: 'Artwork, print from this (JPEG, 150 dpi, with bleed)',
  [OLD_PRINT_FILE]: 'Artwork, print from this (JPEG, 150 dpi, with bleed)',
  '_Original file': 'Original file',
  '_Original file 2': 'Original file 2',
  '_Artwork PDF (print from this)': 'Artwork PDF, print from this',
  '_Artwork PDF (backup)': 'Artwork PDF (backup)',
  "_Customer's original (backup)": 'Customer’s original (backup)',
  '_Original photo (backup)': 'Customer’s original photo (backup)',
  '_Original photo 2 (backup)': 'Customer’s original photo 2 (backup)',
  'Artwork PDF': 'Print PDF (as ordered)',
  '_Print artwork (with 5 mm bleed)': 'Flat print JPEG (as ordered)',
  _artwork_svg: 'Artwork SVG',
  _artwork_preview: 'Preview picture',
  'Your artwork': 'Preview picture (customer’s copy)',
  _ai_url: 'Chosen drawing, as ordered (about 1K)',
  _ai2_url: 'Chosen drawing 2, as ordered (about 1K)',
  '_Original photo (print from this)': 'Customer’s original photo',
  '_Original photo 2 (print from this)': 'Customer’s original photo 2',
  '_Original file (print from this)': 'Customer’s original file',
};
const SECONDS_PER_ENLARGE = 35;

let session = null;

/* --------------------------------------------------------------- server */

async function getSession() {
  const r = await fetch(setup.session, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
  const j = await r.json().catch(() => null);
  if (r.status === 503 || (j && j.setup)) return { down: true };
  if (!r.ok || !j) return { down: true };
  return j;
}

// A fresh ticket before each call: they last 30 minutes
async function api(path, opts = {}) {
  session = await getSession();
  if (!session.signedIn) throw new Error('You’ve been signed out. Sign in again and reload the page.');
  const r = await fetch(session.api.replace(/\/+$/, '') + path, {
    ...opts,
    headers: { Accept: 'application/json', Authorization: 'Bearer ' + session.ticket, ...(opts.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  const j = await r.json().catch(() => ({}));
  return { status: r.status, json: j };
}

/* ---------------------------------------------------------------- start */

function status(html) {
  const el = $('[data-sp-status]');
  el.innerHTML = html;
  el.hidden = !html;
}

function message(text) {
  const el = $('[data-sp-msg]');
  el.textContent = text || '';
  el.hidden = !text;
}

async function start() {
  try {
    session = await getSession();
  } catch (e) {
    session = { down: true };
  }
  if (session.down) {
    status('The studio server isn’t answering just now. Try again in a minute.');
    return;
  }
  if (!session.signedIn) {
    const back = encodeURIComponent(window.location.pathname + window.location.search);
    status(`Sign in with your staff customer account to use this page. <a href="${esc(setup.login)}?return_url=${back}">Sign in</a>`);
    return;
  }
  let check;
  try {
    check = await api('/studio/order');
  } catch (e) {
    check = { status: 0 };
  }
  if (check.status === 403) {
    status('This page is for the studio.');
    return;
  }
  if (check.status !== 400 && check.status !== 200) {
    status('The studio server isn’t answering just now. Try again in a minute.');
    return;
  }
  status('');
  $('[data-sp-main]').hidden = false;
  if (params.get('studio_test') === '1') $('[data-sp-test]').hidden = false;
  $('[data-sp-find]').addEventListener('submit', (e) => {
    e.preventDefault();
    findOrder($('[data-sp-order]').value);
  });
  $('[data-sp-usejson]').addEventListener('click', useJson);
  $('[data-sp-lines]').addEventListener('click', (e) => {
    const b = e.target instanceof Element ? e.target.closest('[data-sp-make]') : null;
    if (b) makePrint(Number(b.dataset.spMake), b);
    const c = e.target instanceof Element ? e.target.closest('[data-sp-cyo]') : null;
    if (c) makeCyoPrint(Number(c.dataset.spCyo), c);
    const t = e.target instanceof Element ? e.target.closest('[data-sp-town]') : null;
    if (t) makeTownPrint(Number(t.dataset.spTown), t);
  });
  if (params.get('order')) {
    $('[data-sp-order]').value = params.get('order');
    findOrder(params.get('order'));
  }
}

/* ---------------------------------------------------------------- order */

let order = null;

// "BTF7130", "#BTF7130", "btf 7130" and "7130" all mean BTF7130
function orderName(raw) {
  const s = String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return /^\d+$/.test(s) ? 'BTF' + s : s;
}

async function findOrder(raw) {
  const name = orderName(raw);
  message('');
  if (!name) {
    message('Type the order number first, for example BTF7130.');
    return;
  }
  $('[data-sp-lines]').innerHTML = '';
  $('[data-sp-orderhead]').hidden = true;
  message('Looking up ' + name + '…');
  let res;
  try {
    res = await api('/studio/order?name=' + encodeURIComponent(name));
  } catch (e) {
    message(e.message);
    return;
  }
  if (res.status === 404) return message(`There’s no order ${name}. Check the number and try again.`);
  if (res.status === 403) return message('This page is for the studio.');
  if (res.status !== 200) return message('The order could not be read just now (' + (res.json.error || 'status ' + res.status) + '). Try again in a minute.');
  message('');
  show({ name: res.json.name.replace(/^#/, ''), createdAt: res.json.createdAt, lines: res.json.lines || [] });
}

// The test box: a line's properties, a /cart.js line, or a whole /cart.js
function useJson() {
  message('');
  let data;
  try {
    data = JSON.parse($('[data-sp-json]').value);
  } catch (e) {
    message('That isn’t JSON. Paste the properties exactly as /cart.js shows them.');
    return;
  }
  const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [data.properties ? data : { properties: data }];
  const lines = items
    .filter((it) => it && it.properties && Object.keys(it.properties).length)
    .map((it) => ({ id: it.key || '', title: it.product_title || it.title || 'Test line', variant: it.variant_title || '', quantity: it.quantity || 1, properties: it.properties }));
  if (!lines.length) return message('No personalised line found in that JSON.');
  show({ name: 'TEST', createdAt: '', lines, test: true });
}

/* ---------------------------------------------------------------- lines */

const isUrl = (v) => typeof v === 'string' && /^https?:\/\//.test(v);
// A file property's full-size address: the basket rewrites some as ["…_small.jpg", "….jpg"]
function urlOf(v) {
  if (isUrl(v) && !/\s/.test(v.trim())) return v.trim();
  const urls = String(v || '').match(/https?:\/\/[^"',\s\]]+/g) || [];
  return urls.find((u) => !/_small\.[a-z]+$/i.test(u)) || urls[0] || '';
}
// Shopify's CDN hands browsers WebP unless asked for the JPEG
const asJpeg = (u) => (/\.jpe?g$/i.test(u) ? u + '?format=pjpg' : u);
// A Create Your Own line made before the print file: the original and the crop it was cut to
const cyoSource = (p) => urlOf(p['_Original file'] || p["_Customer's original (backup)"] || p['_Original file (print from this)'] || p._artwork_url);
// The boards rebuilt here at full quality, with the maker's own settings module: the Pet portrait
// and the Travel poster (21 Oct 2026), whose AI pictures are enlarged to 4K first
const isPet = (p) => !!(setup.makers && setup.makers[p._maker]);
// Made in my town: the town is the only property it writes that no other board does
const isTown = (line) => line.properties.Town != null && /made.in.my.town/i.test(line.title + ' ' + (line.properties._edit_url || ''));

function sizeOf(line) {
  if (line.variant) return line.variant;
  const d = new URLSearchParams(line.properties._design || '');
  return d.get('size') ? d.get('size').replace(/^./, (c) => c.toUpperCase()) : '';
}

function show(o) {
  order = o;
  const head = $('[data-sp-orderhead]');
  const when = o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  head.innerHTML = `<span class="btf-hb">${esc(o.test ? 'Test line' : o.name)}</span><span>${esc(when)}${when ? ' · ' : ''}${o.lines.length} personalised ${o.lines.length === 1 ? 'board' : 'boards'}</span>`;
  head.hidden = false;
  if (!o.lines.length) {
    message('This order has no personalised boards, so there’s nothing to make here.');
    return;
  }
  $('[data-sp-lines]').innerHTML = o.lines.map((line, i) => lineHtml(line, i)).join('');
}

function lineHtml(line, i) {
  const p = line.properties;
  const art = urlOf(p[PRINT_FILE] || p[OLD_PRINT_FILE]);
  const pic = PREVIEW_PROPS.map((k) => urlOf(p[k])).find(Boolean) || (art ? art.split('?')[0] + '?width=600' : '');
  // What the customer chose, in their words: every property that is not hidden and not a file
  const rows = Object.entries(p).filter(([k, v]) => !k.startsWith('_') && !isUrl(v) && v !== '');
  // Files on the line (the other versions of a drawing are a list of addresses, not one file)
  const files = Object.entries(p)
    .filter(([k, v]) => !/^_ai\d*_versions$/.test(k) && urlOf(v) && (isUrl(v) || /^\s*\[/.test(String(v))))
    .map(([k, v]) => [k, asJpeg(urlOf(v).replace(/\?format=pjpg$/, ''))]);
  const pdf = p['Artwork PDF'] || p['_Artwork PDF (print from this)'] || p['_Artwork PDF (backup)'];
  const printFile = art;
  const download = printFile ? `<a class="btf-btn btf-btn--sm" href="${esc(asJpeg(printFile.replace(/\?format=pjpg$/, '')))}" target="_blank" rel="noopener">Open artwork, print from this (JPEG, 150 dpi)</a>` : '';
  const approved = p['Approved for print'] ? `<p class="btf-sp__hint">Approved for print: ${esc(p['Approved for print'])}</p>` : '';
  const qty = line.quantity > 1 ? ` · ${line.quantity} boards` : '';
  let actions = '';
  if (isPet(p)) {
    const what = p._maker === 'travel-poster' ? 'painting' : 'drawing';
    const ai = p._ai_id ? (p._ai2_id ? 'Enlarges both drawings to 4K first (about 35 seconds each).' : `Enlarges the ${what} to 4K first (about 35 seconds).`) : 'Uses the customer’s original photo.';
    actions = `${download}<button type="button" class="${download ? 'btf-btno' : 'btf-btn'} btf-btn--sm" data-sp-make="${i}">${download ? 'Remake with 4K drawing' : 'Make print file'}</button><span class="btf-sp__hint">${esc(ai)}</span>`;
  } else if (isTown(line) && !download) {
    actions = `<button type="button" class="btf-btn btf-btn--sm" data-sp-town="${i}">Make print file</button><span class="btf-sp__hint">Draws the board again from the customer’s words, in Gill Sans.</span>`;
  } else if (download) {
    actions = download;
  } else if (p._crop && cyoSource(p)) {
    actions = `<button type="button" class="btf-btn btf-btn--sm" data-sp-cyo="${i}">Make print file</button><span class="btf-sp__hint">Crops the customer’s original to the board plus bleed, at 150 dpi.</span>`;
  } else if (isUrl(pdf)) {
    actions = `<a class="btf-btn btf-btn--sm" href="${esc(pdf)}" target="_blank" rel="noopener">Open print PDF</a>`;
  } else {
    actions = `<span class="btf-sp-line__error">No print file on this line${p._artwork_pending ? ': ' + esc(p._artwork_pending) : ''}.</span>`;
  }
  return `<li class="btf-sp-line" data-sp-line="${i}">
    <div class="btf-sp-line__pic" data-sp-pic>${pic ? `<img src="${esc(pic)}" alt="The board as ordered" loading="lazy">` : '<span>No preview on this line</span>'}</div>
    <div>
      <span class="btf-lbl btf-sp-line__n">Board ${i + 1}</span>
      <h2 class="btf-hb btf-sp-line__title">${esc(line.title)}</h2>
      <p class="btf-sp-line__size">${esc(sizeOf(line))}${esc(qty)}</p>
      ${rows.length ? `<dl class="btf-sp-line__rows">${rows.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join('')}</dl>` : ''}
      ${approved}
      <div class="btf-sp-line__actions">${actions}</div>
      <div data-sp-out></div>
      ${files.length ? `<ul class="btf-sp-line__files">${files.map(([k, v]) => `<li><a href="${esc(v)}" target="_blank" rel="noopener">${esc(FILE_LABELS[k] || k.replace(/^_/, ''))}</a></li>`).join('')}</ul>` : ''}
    </div>
  </li>`;
}

/* ------------------------------------------------- pet portraits: rebuild */

// A bare Pet portrait maker, out of sight on this page, for one board: the engine restores the
// line into it and makes the files (a storefront page cannot be opened in a frame)
function makerFor(maker) {
  const el = root.querySelector('template[data-sp-maker]').content.firstElementChild.cloneNode(true);
  if (setup.makers && setup.makers[maker]) el.dataset.config = setup.makers[maker];
  document.body.append(el);
  const ready = new Promise((resolve, reject) => {
    const t0 = Date.now();
    const look = () => {
      if (el.studio) return resolve(el.studio);
      if (Date.now() - t0 > 60000) return reject(new Error('the maker did not start'));
      setTimeout(look, 200);
    };
    look();
  });
  return { el, ready };
}

async function enlarge(id, version, say) {
  const t0 = Date.now();
  const tick = setInterval(() => say(Math.round((Date.now() - t0) / 1000)), 1000);
  try {
    const res = await api('/enlarge', { method: 'POST', body: JSON.stringify({ id, version: Number(version) || 1, size: '4K' }) });
    if (res.status === 200 && res.json.url) return res.json.url;
    if (res.status === 403) throw new Error('only studio staff can enlarge drawings');
    if (res.status === 404) throw new Error('the AI server no longer has this drawing (pictures are kept 120 days)');
    throw new Error('the AI server could not enlarge the drawing (' + (res.json.error || 'status ' + res.status) + '); try again');
  } finally {
    clearInterval(tick);
  }
}

const sizeWord = (s) => String(s || '').split(' ')[0].toLowerCase();

async function makePrint(i, button) {
  const line = order.lines[i];
  const p = line.properties;
  const li = root.querySelector(`[data-sp-line="${i}"]`);
  const out = li.querySelector('[data-sp-out]');
  const progress = (text) => (out.innerHTML = `<p class="btf-sp-line__progress" role="status">${esc(text)}</p>`);
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  let maker = null;
  try {
    // The drawings to enlarge: _ai_* for the first portrait, _ai2_* for a second
    const drawings = ['', '2'].filter((s) => p[`_ai${s}_id`]).map((s) => ({ sfx: s, id: p[`_ai${s}_id`], version: p[`_ai${s}_version`] || '1' }));
    const f = makerFor(p._maker);
    maker = f.el;
    const big = {};
    for (let k = 0; k < drawings.length; k++) {
      const d = drawings[k];
      const of = drawings.length > 1 ? ` ${k + 1} of ${drawings.length}` : '';
      progress(`Enlarging drawing${of} to 4K for print… (about ${SECONDS_PER_ENLARGE} seconds; quicker if it’s been done before)`);
      big[d.sfx] = await enlarge(d.id, d.version, (sec) => progress(`Enlarging drawing${of} to 4K for print… ${sec} s (about ${SECONDS_PER_ENLARGE} seconds; quicker if it’s been done before)`));
    }
    progress('Starting the maker…');
    const hook = await f.ready;
    progress(drawings.length ? 'Rebuilding the board from the order…' : 'Rebuilding the board with the customer’s original photo…');
    const got = await hook.restore(p);
    if (got.problems.length) throw new Error(got.problems.join(' '));
    progress(drawings.length ? 'Making the print file with the 4K drawing… (cutting it out at full size can take a little while)' : 'Making the print file…');
    const made = await hook.build(big);
    const base = [order.name, i + 1, p._maker, sizeWord(got.size || sizeOf(line))].join('-');
    const blobUrl = (b, type) => URL.createObjectURL(new Blob([b], { type }));
    const pdfUrl = blobUrl(made.files.pdf, 'application/pdf');
    const jpgUrl = made.files.flat ? blobUrl(made.files.flat, 'image/jpeg') : '';
    const pngUrl = blobUrl(made.files.png, 'image/png');
    li.querySelector('[data-sp-pic]').innerHTML = `<img src="${pngUrl}" alt="The rebuilt board">`;
    const cm = made.cm || got.cm;
    const pics = made.pictures.map((pc, n) => {
      const what = pc.what === 'drawing' ? (pc.big ? 'Drawing, enlarged to 4K' : 'Drawing (not enlarged)') : 'Customer’s photo (original file)';
      const verdict = pc.pdfDpi >= 150 ? '<span class="is-ok">Print-ready.</span>' : '<span class="is-soft">May print soft.</span>';
      const label = made.pictures.length > 1 ? `Picture ${n + 1}: ` : '';
      return `<p>${verdict} ${esc(label + what)}: ${esc(pc.px)} px. On the board it’s drawn ${esc(pc.frameCm[0])} × ${esc(pc.frameCm[1])} cm (in its frame), so it prints at about <b>${esc(pc.dpi)} dpi</b>; the PDF holds it at ${esc(pc.pdfDpi)} dpi.</p>`;
    });
    out.innerHTML = `<div class="btf-sp-line__done">
      <span class="btf-lbl">Print file ready</span>
      <p>${esc(got.design)} · ${esc(got.colour)} · ${esc(got.size)} ${esc(got.format.toLowerCase())}, ${esc(cm[0])} × ${esc(cm[1])} cm plus 5 mm bleed.</p>
      ${pics.join('')}
      <div class="btf-sp-line__actions" style="margin-top: 12px;">
        ${jpgUrl ? `<a class="btf-btn btf-btn--sm" href="${jpgUrl}" download="${esc(base)}-print-150dpi.jpg">Download print file (JPEG, 150 dpi)</a>` : ''}
        <a class="${jpgUrl ? 'btf-btno btf-btno--sm' : 'btf-btn btf-btn--sm'}" href="${pdfUrl}" download="${esc(base)}-print.pdf">Download print PDF</a>
      </div>
    </div>`;
    button.textContent = 'Make it again';
  } catch (e) {
    out.innerHTML = `<p class="btf-sp-line__error" role="alert">The print file couldn’t be made: ${esc(String(e && e.message ? e.message : 'something went wrong').replace(/\.$/, ''))}.</p>`;
  } finally {
    if (maker) maker.remove();
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

/* ------------------------------------- Create Your Own: print file from the original */

// Orders placed before the print file went on the line (19 Oct 2026): the original, cut to the
// crop the customer chose (the _crop property's bleed box), at 150 dpi
async function makeCyoPrint(i, button) {
  const line = order.lines[i];
  const p = line.properties;
  const li = root.querySelector(`[data-sp-line="${i}"]`);
  const out = li.querySelector('[data-sp-out]');
  const progress = (text) => (out.innerHTML = `<p class="btf-sp-line__progress" role="status">${esc(text)}</p>`);
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const crop = JSON.parse(p._crop);
    const [bx, by, bw, bh] = crop.bleed_px;
    const [wmm, hmm] = crop.print_mm;
    const PJ = await import((window.btfAssets && window.btfAssets.printJpeg) || './btf-print-jpeg.js');
    progress('Fetching the customer’s original…');
    const bmp = await PJ.loadBitmap(asJpeg(cyoSource(p)));
    const [W, H] = PJ.printPixels(wmm, hmm);
    const kx = W / bw;
    const ky = H / bh;
    const sw = (crop.src_px && crop.src_px[0]) || bmp.width;
    const sh = (crop.src_px && crop.src_px[1]) || bmp.height;
    const made = await PJ.printJpeg({
      wmm,
      hmm,
      maxPx: 60000000,
      progress: (f) => progress(`Making the print file… ${Math.round(f * 100)}%`),
      draw: (ctx, y0) => {
        ctx.translate(0, -y0);
        PJ.drawPhoto(ctx, bmp, -bx * kx, -by * ky, sw * kx, sh * ky, W, H);
      },
    });
    bmp.close();
    const url = URL.createObjectURL(made.blob);
    const base = [order.name, i + 1, 'print', `${wmm}x${hmm}mm`, '150dpi'].join('-');
    const dpi = Math.round(crop.dpi || 0);
    out.innerHTML = `<div class="btf-sp-line__done">
      <span class="btf-lbl">Print file ready</span>
      <p>${esc(made.width)} × ${esc(made.height)} px: ${esc(wmm)} × ${esc(hmm)} mm (the board plus 5 mm bleed) at 150 dpi.${dpi && dpi < 150 ? ` The photo was ${esc(dpi)} dpi at this size, so it’s enlarged; enhance it before printing if it looks soft.` : ''}</p>
      <div class="btf-sp-line__actions" style="margin-top: 12px;">
        <a class="btf-btn btf-btn--sm" href="${url}" download="${esc(base)}.jpg">Download print file (JPEG, 150 dpi)</a>
      </div>
    </div>`;
    button.textContent = 'Make it again';
  } catch (e) {
    out.innerHTML = `<p class="btf-sp-line__error" role="alert">The print file couldn’t be made: ${esc(String(e && e.message ? e.message : 'something went wrong').replace(/\.$/, ''))}.</p>`;
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

/* ----------------------------------------- Made in my town: drawn again from the words */

// The colourway is its own product: "Small 55 × 40 cm / Blue", or the title's "- Blue -"
function townColour(line, T) {
  const words = [...String(line.variant || '').split('/').slice(1), ...String(line.title || '').split(/\s+-\s+/)];
  return words.map((w) => T.colourKey(w)).find((k) => T.COLOURS[k]) || '';
}

async function makeTownPrint(i, button) {
  const line = order.lines[i];
  const p = line.properties;
  const li = root.querySelector(`[data-sp-line="${i}"]`);
  const out = li.querySelector('[data-sp-out]');
  const progress = (text) => (out.innerHTML = `<p class="btf-sp-line__progress" role="status">${esc(text)}</p>`);
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const [T, PJ] = await Promise.all([import(setup.town), import((window.btfAssets && window.btfAssets.printJpeg) || './btf-print-jpeg.js')]);
    if (!T.faceInstalled()) throw new Error('this computer doesn’t have Gill Sans, the board’s lettering. Make it on the studio Mac');
    const colour = townColour(line, T);
    if (!colour) throw new Error('the colour couldn’t be read from “' + line.title + '”');
    const format = T.orientKey(p.Format);
    const size = T.sizeKey(line.variant);
    const cm = T.boardCm(format, size);
    const town = String(p.Town || '').trim();
    const line2 = String(p['Second line'] || '').trim();
    progress('Drawing the board…');
    await document.fonts.ready;
    const svg = T.printFace(cm, town, line2, colour);
    const blob = await PJ.svgPrintJpeg(svg, cm[0] * 10 + 10, cm[1] * 10 + 10, (f) => progress(`Making the print file… ${Math.round(f * 100)}%`));
    const url = URL.createObjectURL(blob);
    const [W, H] = PJ.printPixels(cm[0] * 10 + 10, cm[1] * 10 + 10);
    const words = [town, line2].filter(Boolean).join(' ');
    const base = [order.name, i + 1, 'made-in-my-town', words, colour, size, format].join('-').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    li.querySelector('[data-sp-pic]').innerHTML = `<img src="${url}" alt="The print file">`;
    const name = colour.replace(/^./, (c) => c.toUpperCase());
    out.innerHTML = `<div class="btf-sp-line__done">
      <span class="btf-lbl">Print file ready</span>
      <p>${esc(words)} · ${esc(name)} · ${esc(size)} ${esc(format.toLowerCase())}, ${esc(cm[0])} × ${esc(cm[1])} cm plus 5 mm bleed: ${esc(W)} × ${esc(H)} px at 150 dpi.</p>
      <div class="btf-sp-line__actions" style="margin-top: 12px;">
        <a class="btf-btn btf-btn--sm" href="${url}" download="${esc(base)}-print-150dpi.jpg">Download print file (JPEG, 150 dpi)</a>
      </div>
    </div>`;
    button.textContent = 'Make it again';
  } catch (e) {
    out.innerHTML = `<p class="btf-sp-line__error" role="alert">The print file couldn’t be made: ${esc(String(e && e.message ? e.message : 'something went wrong').replace(/\.$/, ''))}.</p>`;
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

start();
